import React, { useMemo } from "react";
import { useRequest } from "ahooks";
import { api } from "@redux/api";
import { useAuth } from "@/hooks/use-auth";
import { Nopermission } from "@/components/Nopermission";
import { useNavigate } from "react-router-dom";
import { emitEvent } from "@/hooks/useEventBus";
import { useDispatch, useSelector } from "react-redux";
import appActions from "@redux/app/actions";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import SwitchablePieChart from "./SwitchablePieChart";
import ColumnChart from "@containers/UtilityData/Charts/ColumnChart";
import AiChartContextRegistration from "@/containers/facility/AiAssistant/AiChartContextRegistration";

const STATUS_COLORS = {
  "Completed": "#4ade80",
  "Verifying": "#60a5fa",
  "On-Hold": "#9ca3af",
  "InProgress": "#fb923c",
  "Not Started": "#ef4444",
};

const PRIORITY_COLORS = {
  "Urgent": "#ef4444",
  "Important": "#faad14",
  "Low": "#4ade80",
};

const WorkorderChart = () => {
  const workorderPermission = useAuth("workorder", 1);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const facilityId = useSelector((state) => state?.Facility?.selected?.Id);
  const facilityContext = useSelector((state) => getFacilityContext(state));

  const { data: workorderData } = useRequest(
    () => api.allworkorderview(),
    {
      ready: workorderPermission,
      cacheKey: `workorder-combined-chart-data-${facilityId}`,
      staleTime: 1 * 60 * 1000,
    }
  );

  if (!workorderPermission) {
    return <Nopermission />;
  }

  const groupedStatusData = workorderData?.data?.grouped_status_data;
  const groupedPriorityData = workorderData?.data?.grouped_priority_data;
  const past7Past30Data = workorderData?.data?.past7_past30;

  if (!groupedStatusData && !groupedPriorityData && !past7Past30Data) {
    return null;
  }

  const handleStatusClick = (e) => {
    emitEvent("CLOSE_WORKORDER");
    const url =
      buildFacilityPath("workorder", {
        ...facilityContext,
        query: { status: e.data.name },
      }) || "/switch-view";
    navigate(url);
    dispatch(appActions.changeCurrent(["workorder"]));
  };

  const handlePriorityClick = (e) => {
    emitEvent("CLOSE_WORKORDER");
    const url =
      buildFacilityPath("workorder", {
        ...facilityContext,
        query: { priority: e.data.name },
      }) || "/switch-view";
    navigate(url);
    dispatch(appActions.changeCurrent(["workorder"]));
  };

  const handleWorkSummaryClick = (e) => {
    emitEvent("CLOSE_WORKORDER");
    let params = "";
    switch (e.date_range) {
      case "Past 30 Days":
        params += "modify_date=" + 30;
        break;
      case "Past 7 Days":
        params += "modify_date=" + 7;
        break;
      case "Past 90 Days":
        params += "modify_date=" + 90;
        break;
      default:
        break;
    }
    params += "&status=" + e.status;

    const url =
      buildFacilityPath("workorder", {
        ...facilityContext,
        query: params,
      }) || "/switch-view";
    navigate(url);
    dispatch(appActions.changeCurrent(["workorder"]));
  };

  const configs = [
    groupedStatusData && {
      key: "status",
      chartId: "dashboard.workorders.status",
      title: "Workorder By Status",
      label: "Status",
      data: groupedStatusData,
      colors: STATUS_COLORS,
      onClick: handleStatusClick,
    },
    groupedPriorityData && {
      key: "priority",
      chartId: "dashboard.workorders.priority",
      title: "Workorder By Priority",
      label: "Priority",
      data: groupedPriorityData,
      colors: PRIORITY_COLORS,
      onClick: handlePriorityClick,
    },
    past7Past30Data && {
      key: "summary",
      chartId: "dashboard.workorders.summary",
      label: "Summary",
      render: () => (
        <ColumnChart
          data={past7Past30Data}
          title="Workorder Summary"
          slider={false}
          isStack={false}
          isGroup={true}
          handleChartClick={handleWorkSummaryClick}
        />
      ),
    },
  ].filter(Boolean);

  return (
    <>
      {groupedStatusData ? (
        <AiChartContextRegistration showAction={false}
          context={{
            pageId: "page.facility.dashboard",
            chart: {
              chartId: "dashboard.workorders.status",
              title: "Workorder By Status",
              aliases: ["workorder status", "status pie", "工单状态图", "工单状态分布"],
              chartType: "pie",
              description: "Current workorder count grouped by status.",
              sourceOperationIds: ["work_order.group_by_status"],
              series: [{
                name: "Workorders",
                unit: "count",
                points: groupedStatusData.map((item) => ({ label: item.type, value: item.value })),
              }],
              completeness: "complete",
            },
          }}
        />
      ) : null}
      {groupedPriorityData ? (
        <AiChartContextRegistration showAction={false}
          context={{
            pageId: "page.facility.dashboard",
            chart: {
              chartId: "dashboard.workorders.priority",
              title: "Workorder By Priority",
              aliases: ["workorder priority", "priority pie", "工单优先级图", "工单优先级分布"],
              chartType: "pie",
              description: "Current workorder count grouped by priority.",
              sourceOperationIds: ["work_order.group_by_priority"],
              series: [{
                name: "Workorders",
                unit: "count",
                points: groupedPriorityData.map((item) => ({ label: item.type, value: item.value })),
              }],
              completeness: "complete",
            },
          }}
        />
      ) : null}
      {past7Past30Data ? <AiChartContextRegistration showAction={false} context={{
        pageId: "page.facility.dashboard",
        chart: {
          chartId: "dashboard.workorders.summary", title: "Workorder Summary", chartType: "bar",
          description: "Created, modified and completed work orders in overlapping recent periods.",
          sourceOperationIds: ["work_order.group_by_status"],
          series: ["Created", "Modified", "Completed"].map(status => ({
            name: status, unit: "count", points: ["Past 7 Days", "Past 30 Days", "Past 90 Days"].map(period => ({
              label: period, value: past7Past30Data.find(item => item.date_range === period && item.status === status)?.value ?? null,
            })),
          })),
          completeness: "unknown", limitations: ["Recent periods overlap and must not be summed. Missing source values are not measured zero."],
        },
      }} /> : null}
      <SwitchablePieChart configs={configs} defaultActiveKey="status" />
    </>
  );
};

export default WorkorderChart;
